<template>
  <div class="card pa-20 card-max-1440">
    <header class="card-title has-text-justified mb-20">
      <p class="has-text-grid-1 has-text-weight-bold is-size-6">
        {{ $t('listeDevisAchat.titre') }}
      </p>
    </header>
    <section class="level mb-20">
      <div class="level-left">
        <div class="level-item">
          <b-field>
            <b-select
              class="action-groupee-select"
              :placeholder="$t('general.actionGroupe.placeholder')"
              v-model="actionGroupee"
              :disabled="!itemsSelected.length"
              expanded>
              <option value="telecharger">
                {{ $t('listeDevisAchat.actionGroupee.telecharger') }}
              </option>
            </b-select>
          </b-field>
        </div>
        <div class="level-item">
          <b-button
            type="is-primary"
            :disabled="!(actionGroupee && itemsSelected.length)"
            @click="appliquerActionGroupee">
            <span class="has-text-weight-bold">
              {{ $t('general.actionGroupe.actionApplication') }}
            </span>
          </b-button>
        </div>
      </div>
    </section>
    <section>
      <b-table
        :data="items"
        :loading="loading"
        :checked-rows.sync="itemsSelected"
        backend-sorting
        @sort="onSort"
        checkable>
        
        <b-table-column
          v-for="column in constants.COLUMNS"
          :key="column.field"
          :label="column.sortable ? `↑↓ ${$t(column.label)}` : $t(column.label)"
          v-bind="column">
          <template v-if="column.searchable" #searchable>
            <qd-filter-table-row
              v-model="filter[column.searchField]"
              :column="column"
              @reloadItemsForSearch="reloadItemsForSearch"/>
          </template>
          <template v-slot="{ row }">
            <div v-if="column.action">
              <b-button
                class="has-background-transparent has-text-primary no_border"
                size="is-medium"
                :icon-right="column.icon"
                @click="actionColumn(column.field, row)"/>
            </div>
            <span v-else>
              {{ getValueColumn(row, column) }}
            </span>
          </template>
        </b-table-column>
        <template #empty>
          <section class="section">
            <div class="content has-text-grey has-text-centered">
              <p>{{ $t('general.autocomplete.noRslt') }}</p>
            </div>
          </section>
        </template>
      </b-table>
      <qd-pagination
        :page="page"
        :perPage="perPage"
        :total="totalCount"
        @page-change="reloadItemsForPage($event, perPage)"
        @perPage-change="reloadItemsForPage(page, $event)"
      />
    </section>
    <footer class="buttons mt-20">
      <b-button
        type="is-transparent"
        class="has-text-default"
        @click="$emit('close')">
        {{ $t('general.btnFermer') }}
      </b-button>
    </footer>
    <div 
      v-if="isModalEmailOpen"
      class="modal-email-devis column columns is-width-100p is-marginless is-overflow-y-active">
      <div class="column is-8 has-background-white is-paddingless modal-email-content">
        <modal-envoie-mail
          v-if="devisEmailActuel"
          :idRecom="devisEmailActuel.id"
          :is-send="isModalEmailOpen"
          :recomPdf="pdfDevisActuel"
          :societeId="societeId"
          :mettreEdition="false"
          :email-destinataire="emailFournisseur"
          :objet-email="objetEmail"
          :titreModal="$t('listeDevisAchat.mail.titre')"
          @change-send="closeModalEmail">
        </modal-envoie-mail>
      </div>
      <div 
        class="column has-background-transparent pointer full-height-viewport"
        @click="closeModalEmail">
      </div>
    </div>
  </div>
</template>

<script>
import get from 'lodash/get';
import debounce from 'lodash/debounce';
import { getAll } from '@/api/devis/index';
import transform from '@/services/general/transform';
import gestionErreur from '@/services/general/gestionErreur';
import QdPagination from '@/components/general/qdPagination.vue';
import QdFilterTableRow from '@/components/general/qdFilterTableRow.vue';
import ModalEnvoieMail from '@/components/recommandation/detailRecommandation/modalEnvoieMail.vue';
import { COLUMNS } from '@/configuration/listeDevisAchatConfig';
import { DEVIS_ACHAT_ETAT } from '@/configuration/devisConf';
import { transformToGescomAchat } from '@/services/devisAchat/devisAchatTransform';
import {
  editionPdfDevisAchat,
  prepareEmailDevisAchat,
  telechargerDevisAchatZip,
} from '@/services/devisAchat/devisAchatActions';
import {
  ETAT_BROUILLON,
  ETAT_ENREGISTRE,
  ETAT_SANSSUITE,
  MODULE_ACHAT,
  SENS_ACHAT,
  NORMAL,
  TYPE_REFERENCE_EXTERNE_VENTE_DEVIS,
} from '@/configuration/devisConf';

export default {
  name: 'ModalListeDevisAchat',
  components: {
    QdPagination,
    QdFilterTableRow,
    ModalEnvoieMail,
  },
  props: {
    societeId: {
      type: String,
      required: true,
    },
    referenceExterne: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      page: 1,
      perPage: 10,
      totalCount: 0,
      loading: false,
      sortOrder: 'desc',
      sortField: 'creationTime',
      items: [],
      itemsSelected: [],
      actionGroupee: null,
      constants: { COLUMNS },
      filter: {
        code: '',
        referenceHavas: '',
        etat: '',
        fournisseur: '',
        dateCreation: '',
        montant: '',
      },
      isModalEmailOpen: false,
      devisEmailActuel: null,
      pdfDevisActuel: [],
      emailFournisseur: '',
    };
  },
  computed: {
    objetEmail() {
      if (this.devisEmailActuel) {
        return this.$t('listeDevisAchat.mail.objet', { codeDevis: this.devisEmailActuel.code });
      }
      return '';
    },
  },
  methods: {
    reloadItemsForSearch: debounce(async function reloadFunc() {
      this.page = 1;
      await this.loadItems();
    }, 1000),
    getValueColumn(row, column) {
      if (column.field === 'etat') {
        return this.getEtatLibelle(row.etat);
      }
      
      if (column.format) return column.format(row);
      return get(row, column.field);
    },
    getEtatLibelle(etat) {
      const find = DEVIS_ACHAT_ETAT.find((el) => el.code === etat);
      return find ? this.$t(find.libelle) : '';
    },
    onSort(field, order) {
      this.sortField = field;
      this.sortOrder = order;
      this.loadItems();
    },
    async reloadItemsForPage(page, perPage) {
      this.page = page;
      this.perPage = perPage;
      await this.loadItems();
    },
    async loadItems() {
      this.loading = true;
      try {
        let etatsAFiltrer;
        if (this.filter.etat) {
          etatsAFiltrer = [this.filter.etat];
        } else {
          etatsAFiltrer = [ETAT_BROUILLON, ETAT_ENREGISTRE, ETAT_SANSSUITE];
        }

        const serverTableData = {
          page: this.page,
          limit: this.perPage,
          orderBy: this.sortField,
          ascending: this.sortOrder === 'asc',
          query: {
            code: this.filter.code || null,
            referenceHavas: this.filter.referenceHavas || null,
            pointDeVente: this.filter.fournisseur || null,
            dateCreation: this.filter.dateCreation || null,
            montant: this.filter.montant || null,
            
            etat: etatsAFiltrer,
            sousType: [NORMAL],
            typeReferenceExterne: TYPE_REFERENCE_EXTERNE_VENTE_DEVIS,
            
            sens: SENS_ACHAT,
            module: MODULE_ACHAT,
            societeId: this.societeId,
          },
        };

        if (this.referenceExterne) {
          serverTableData.query.referenceExterne = this.referenceExterne;
        }

        const payload = transformToGescomAchat(serverTableData);
        const response = await getAll(transform(payload));
        
        this.items = response.data.result.items || [];
        this.totalCount = response.data.result.totalCount || 0;
      } catch (error) {
        gestionErreur('Erreur lors de la récupération des devis d\'achat', error);
      } finally {
        this.loading = false;
      }
    },
    async editerDevis(devis) {
      const loadingComponent = this.$buefy.loading.open();
      try {
        await editionPdfDevisAchat(devis);
      }finally {
        loadingComponent.close();
      }
    },
    async envoyerEmailDevis(devis) {
      const loadingComponent = this.$buefy.loading.open();
      try {
        const { pdfFile, emailFournisseur } = await prepareEmailDevisAchat(devis);
        
        this.devisEmailActuel = devis;
        this.pdfDevisActuel = [pdfFile];
        this.emailFournisseur = emailFournisseur;
        this.isModalEmailOpen = true;
      }finally {
        loadingComponent.close();
      }
    },
    actionColumn(action, devis) {
      if (action === 'editer') {
        this.editerDevis(devis);
      } else if (action === 'envoyerEmail') {
        this.envoyerEmailDevis(devis);
      }
    },
    closeModalEmail() {
      this.isModalEmailOpen = false;
      this.devisEmailActuel = null;
      this.pdfDevisActuel = [];
      this.emailFournisseur = '';
    },
    async appliquerActionGroupee() {
      if (this.actionGroupee === 'telecharger') {
        await this.telechargerSelection();
      }
    },
    async telechargerSelection() {
      if (this.itemsSelected.length === 0) {
        this.$buefy.toast.open({
          message: this.$t('listeDevisAchat.messages.selectionVide'),
          type: 'is-warning',
        });
        return;
      }

      const loadingComponent = this.$buefy.loading.open();
      try {
        const nbDevis = await telechargerDevisAchatZip(this.itemsSelected);

        this.itemsSelected = [];
        this.actionGroupee = null;

        this.$buefy.toast.open({
          message: this.$t('listeDevisAchat.messages.zipSuccess', { nb: nbDevis }),
          type: 'is-success',
        });
      }finally {
        loadingComponent.close();
      }
    },
  },
  mounted() {
    if (this.societeId) {
      this.loadItems();
    }
  },
};
</script>

<style scoped>

</style>